// frontend/src/components/VerificationCode.js
// UrbanQ v1.8.9.4.5.7 - Build 2026-02-19
import React, { useState, useRef, useEffect } from 'react';
import './VerificationCode.css';

const VerificationCode = ({ email, userId, onVerify, onResend, onBack, error, loading: externalLoading }) => {
    const [code, setCode] = useState(['', '', '', '', '', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        // Timer para reenvio
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);

    useEffect(() => {
        // Focus en primer input
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleChange = (index, value) => {
        // Solo permitir alfanumericos
        const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

        if (sanitized.length <= 1) {
            const newCode = [...code];
            newCode[index] = sanitized;
            setCode(newCode);

            // Mover al siguiente input
            if (sanitized && index < 9) {
                inputRefs.current[index + 1]?.focus();
            }

            // Auto-submit si esta completo
            if (index === 9 && sanitized) {
                const fullCode = newCode.join('');
                if (fullCode.length === 10) {
                    handleSubmit(fullCode);
                }
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (pasted.length === 10) {
            const newCode = pasted.split('');
            setCode(newCode);
            handleSubmit(pasted);
        }
    };

    const handleSubmit = async (fullCode = code.join('')) => {
        if (fullCode.length !== 10 || loading || externalLoading) return;

        setLoading(true);
        await onVerify(fullCode);
        setLoading(false);
    };

    const handleResend = async () => {
        if (!canResend || resendLoading) return;
        setResendLoading(true);
        setCanResend(false);
        setResendTimer(60);
        setCode(['', '', '', '', '', '', '', '', '', '']);
        await onResend();
        setResendLoading(false);
        inputRefs.current[0]?.focus();
    };

    const isLoading = loading || externalLoading;

    return (
        <div className="verification-container">
            <div className="verification-card">
                <div className="verification-icon">&#128274;</div>

                <h1 className="verification-title">Verificacion de Seguridad</h1>

                <p className="verification-subtitle">
                    Ingresa el codigo de 10 caracteres que enviamos a:
                </p>
                <p className="verification-email">{email}</p>

                {error && (
                    <div className="verification-error">
                        <span>&#9888;</span> {error}
                    </div>
                )}

                <div className="verification-inputs" onPaste={handlePaste}>
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={el => inputRefs.current[index] = el}
                            type="text"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className={`verification-input ${digit ? 'filled' : ''}`}
                            disabled={isLoading}
                            autoComplete="off"
                        />
                    ))}
                </div>

                <button
                    className="verification-submit"
                    onClick={() => handleSubmit()}
                    disabled={isLoading || code.join('').length !== 10}
                >
                    {isLoading ? (
                        <span className="loading-spinner"></span>
                    ) : (
                        'Verificar Codigo'
                    )}
                </button>

                <div className="verification-actions">
                    <button
                        className="verification-resend"
                        onClick={handleResend}
                        disabled={!canResend || resendLoading}
                    >
                        {resendLoading ? (
                            'Enviando...'
                        ) : canResend ? (
                            '&#128231; Reenviar codigo'
                        ) : (
                            `Reenviar en ${resendTimer}s`
                        )}
                    </button>

                    <button className="verification-back" onClick={onBack}>
                        &#8592; Volver al login
                    </button>
                </div>

                <p className="verification-hint">
                    &#128161; El codigo expira en 10 minutos
                </p>
            </div>
        </div>
    );
};

export default VerificationCode;
